// TODAY'S VERSION (v1), kept beside the v1.1 one until the press (lib/release/next).
// Copied from main as it was on 2026-10-23; everyone who is not a test account sees this one.
// netti's tours: a walk through something new, the way the first-run walk feels
// (no React, no I/O, so the server, the shell and a test all read the same list).
//
// John, 2026-10-19: "whenever we launch new spaces or new features, netti can
// greet you: welcome to this new update. you can click next to have netti open
// different spaces and walk you through". A tour is a short script of beats.
// Each beat is one netti line, spoken into the same guide the first-run walk uses
// (components/onboarding/NettiGuide), and may OPEN a place first, so netti walks
// you there rather than describing it from afar.
//
// Three kinds:
//   update   greets you once, the first time you are in the dashboard after it
//            shipped, if you can see the feature it is about
//   space    plays once, the first time you stand in a place it is about
//   guide    only when asked (a "walk me through it" link); never on its own
//
// What a person has seen is kept in user_settings.tours (migration 154) as
// { [tourId]: ISO time }, so a tour greets once across every device.

export type TourKind = "update" | "space" | "guide";

/** the place a beat opens: a path, or a token the shell fills in (the first agent you keep) */
export type TourPlace = string;

export interface TourBeat {
  /** stable within its tour */
  id: string;
  /** netti's line. `{name}` is the person's name, `{agent}` the agent being shown */
  text: string;
  /** a place to open before the line is spoken. `{agent}` is the agent's own path (/agents/<slug>) */
  goto?: TourPlace;
  /** the beat only plays when this holds (an agent to show, say); otherwise it is stepped over */
  needs?: "agent" | "no-agent";
  /** the chips under the line; default: next (and on the last beat, done) */
  actions?: Array<{ label: string; act: TourAct }>;
}

export type TourAct = "next" | "later" | "done" | "make-agent";

export interface Tour {
  id: string;
  kind: TourKind;
  /** a feature id (lib/features/registry F): the tour exists only for someone who can see it */
  feature?: string;
  /** when it shipped (an update tour greets only people who were here before; anyone newer meets it in the first-run walk) */
  since: string;
  /** a space tour plays the first time the path matches */
  at?: RegExp;
  /** seeing this tour counts as having seen these too (a long walk covers a short one) */
  covers?: string[];
  beats: TourBeat[];
}

// ── the agents walk (2026-10-19) ─────────────────────────────────────────────
// What an agent's memory is, and how it is laid out: four spaces, sorted memory,
// one access level, its own project. Shared by the update greeting and the guide.

const AGENTS_WALK: TourBeat[] = [
  {
    id: "where",
    goto: "/connections",
    text: "your agents live here in connections, under the agents you keep. each one has a memory of its own, sealed to you. no other tool reads it unless you say so.",
  },
  {
    id: "make",
    needs: "no-agent",
    text: "you have none yet. make one: a name and what it is for is all it needs. then paste the lines it gives you into the ai you code with, and it does the wiring.",
    actions: [
      { label: "make one", act: "make-agent" },
      { label: "next", act: "next" },
    ],
  },
  {
    id: "memory",
    needs: "agent",
    goto: "{agent}/memory",
    text: "this is {agent}'s memory. everything it keeps is sorted by what kind of thing it is: how it works, what it knows, what people like, what happened. it writes into a project of its own, so i fold it like the rest of your memory.",
  },
  {
    id: "access",
    needs: "agent",
    goto: "{agent}/access",
    text: "access is one choice. sealed reads nothing of you. asks first waits for your yes before anything about you reaches your memory. trusted files it on its own. the four lines underneath say exactly what gets in and out.",
  },
  {
    id: "activity",
    needs: "agent",
    goto: "{agent}/activity",
    text: "activity is everything it did: each call, what it carried, and what it runs on a beat.",
  },
  {
    id: "settings",
    needs: "agent",
    goto: "{agent}/settings",
    text: "settings holds its keys. a key is the agent's password to this memory: when you make an agent, it hands you a few lines with the key inside to paste into the ai you code with, and that ai wires it in for you.",
  },
  {
    id: "end",
    text: "that's the whole of it. ask me about any of it in the corner, any time.",
    actions: [{ label: "done", act: "done" }],
  },
];

export const TOURS: Tour[] = [
  {
    id: "agents-update",
    kind: "update",
    feature: "agents",
    since: "2026-10-19",
    covers: ["agents-guide", "agent-room"],
    beats: [
      {
        id: "hello",
        text: "hi {name}. something new: memory for the agents you keep. want me to show you round? it takes a minute.",
        actions: [
          { label: "show me", act: "next" },
          { label: "later", act: "later" },
        ],
      },
      ...AGENTS_WALK,
    ],
  },
  {
    id: "agents-guide",
    kind: "guide",
    feature: "agents",
    since: "2026-10-19",
    covers: ["agents-update", "agent-room"],
    beats: [
      {
        id: "hello",
        text: "happy to. here is how an agent's memory is made, and where everything lives.",
      },
      ...AGENTS_WALK,
    ],
  },
  {
    id: "agent-room",
    kind: "space",
    feature: "agents",
    since: "2026-10-19",
    at: /^\/agents\/[^/]+\/(memory|access|activity|settings)$/,
    beats: [
      {
        id: "hello",
        text: "this is {agent}'s own memory. four spaces along the top: memory, access, activity and settings.",
      },
      {
        id: "access",
        goto: "{agent}/access",
        text: "access is the one to know first: what {agent} may read of you, and what it may write about you.",
      },
      {
        id: "memory",
        goto: "{agent}/memory",
        text: "and memory is what it keeps, sorted by kind. that's it, the rest is yours to look round.",
        actions: [{ label: "done", act: "done" }],
      },
    ],
  },
];

export const TOUR_BY_ID = new Map(TOURS.map((t) => [t.id, t]));

// ── which tour is due ────────────────────────────────────────────────────────

export interface TourContext {
  /** tour id → when it was seen */
  seen: Record<string, string>;
  /** may this person see a feature? */
  canSee: (feature: string) => boolean;
  /** the shell's path */
  path: string;
  /** when the account was made (an update greets only people who were here before it shipped) */
  joinedAt?: string | null;
}

/** has this tour (or one that covers it) been seen? */
export function seenTour(t: Tour, seen: Record<string, string>): boolean {
  if (seen[t.id]) return true;
  return TOURS.some((o) => o.covers?.includes(t.id) && !!seen[o.id]);
}

/** the one tour that should start on its own now, or null. Updates first (they greet), then a space tour for where you stand. */
export function dueTour(ctx: TourContext, tours: readonly Tour[] = TOURS): Tour | null {
  const open = (t: Tour) => (!t.feature || ctx.canSee(t.feature)) && !seenTour(t, ctx.seen);
  for (const t of tours) {
    if (t.kind !== "update" || !open(t)) continue;
    // someone who joined after it shipped meets the feature as it is, not as news
    if (ctx.joinedAt && ctx.joinedAt.slice(0, 10) > t.since) continue;
    return t;
  }
  for (const t of tours) {
    if (t.kind === "space" && t.at && open(t) && t.at.test(ctx.path)) return t;
  }
  return null;
}

/** what a tour's seen-map looks like once it is seen: itself and everything it covers, at the same moment */
export function markSeen(seen: Record<string, string>, t: Tour, at: string): Record<string, string> {
  const next = { ...seen, [t.id]: at };
  for (const c of t.covers ?? []) if (!next[c]) next[c] = at;
  return next;
}

// ── a beat, filled in ────────────────────────────────────────────────────────

export interface TourFill {
  name: string;
  /** the agent being shown: its name and its own path, when there is one */
  agent?: { name: string; path: string } | null;
}

/** the beats that play for this person (a beat that needs an agent is stepped over when there is none, and the other way) */
export function beatsFor(t: Tour, fill: TourFill): TourBeat[] {
  const has = !!fill.agent;
  return t.beats.filter((b) => !b.needs || (b.needs === "agent" ? has : !has));
}

/** a beat's words and place with the tokens filled */
export function fillBeat(b: TourBeat, fill: TourFill): { text: string; goto: string | null } {
  const agentName = fill.agent?.name ?? "your agent";
  const text = b.text.replace(/\{name\}/g, fill.name || "there").replace(/\{agent\}/g, agentName);
  let goto: string | null = b.goto ?? null;
  if (goto && goto.includes("{agent}")) goto = fill.agent ? goto.replace("{agent}", fill.agent.path.replace(/\/+$/, "")) : null;
  return { text, goto };
}

/** the chips a beat offers: its own, or next (done on the last) */
export function actionsFor(b: TourBeat, last: boolean): Array<{ label: string; act: TourAct }> {
  if (b.actions?.length) return b.actions;
  return last ? [{ label: "done", act: "done" }] : [{ label: "next", act: "next" }, { label: "that's enough", act: "later" }];
}
